import { FC, useState } from 'react';
import cx from 'classnames';
import { PostCard } from 'src/components/Blog';
import { postCardAdapter } from 'src/components/Blog/PostCard/adapters';
import { formatTag } from 'src/components/Blog/PostCard/PostCard';
import { ContentfulToolbar } from 'src/components/ContentfulToolbar';
import Pagination from 'src/components/Pagination/Pagination';
import { Link } from 'src/components/Inputs';
import { TemplateWrapper } from 'src/templates';
import type { Entry, Sys } from 'src/types/contentful-sdk-compat';
import { ContentfulTemplates } from 'src/lib/constants';
import LegacyScripts from 'src/components/LegacyScripts';
import styles from './BlogOverviewTemplate.module.scss';

interface PageMetaFields {
  heading?: string;
  metaDescription?: string;
  metaTitle?: string;
}

interface BlogPost {
  fields?: PageMetaFields;
  heading?: string;
  metaDescription?: string;
  metaTitle?: string;
  sys?: Sys;
}

interface BlogOverviewTemplateProps {
  posts: Entry<BlogPost>[];
  fields: {
    metaTitle?: string;
    metaDescription?: string;
    pageSlug?: string;
    hasFooterCta?: boolean;
  };
  heading: string;
  pageNum: number;
  totalPages: number;
  basePath: string;
  allTags?: string[];
  activeTag?: string;
  noIndex?: boolean;
  robotsContent?: string;
  cmsTheme?: string;
}

const BlogOverviewTemplate: FC<BlogOverviewTemplateProps> = ({
  fields,
  posts = [],
  heading,
  pageNum,
  totalPages,
  basePath,
  allTags: allTagsProp,
  activeTag = 'all',
  noIndex = false,
  robotsContent,
  cmsTheme = 'web 2.0',
}) => {
  const adaptedPosts = posts
    .map((post) => {
      try {
        return postCardAdapter(post?.fields);
      } catch {
        return null;
      }
    })
    .filter(Boolean);

  const allTags = allTagsProp ?? [
    ...new Set(
      adaptedPosts
        .flatMap((post) => {
          return post.tags || [];
        })
        .filter(Boolean),
    ),
  ];

  const TAG_LIMIT = 8;
  const [showAllTags, setShowAllTags] = useState(false);
  const hasMoreTags = allTags.length > TAG_LIMIT;

  return (
    <TemplateWrapper
      meta-title={fields?.metaTitle}
      meta-description={fields?.metaDescription}
      hasFooterCta={fields?.hasFooterCta}
      className='blog'
      noIndex={noIndex}
      robotsContent={robotsContent}
      cmsTheme={cmsTheme}
    >
      <div className={styles.pageWrapper}>
        <h1
          className={styles.pageHeading}
          dangerouslySetInnerHTML={{ __html: heading }}
        />

        <div
          data-testid={ContentfulTemplates.BLOG_OVERVIEW}
          className={styles.layout}
        >
          <aside className={styles.sidebarScroll}>
            <div className={styles.sidebar}>
              <Link href='/blog/'>
                <a
                  className={cx(
                    styles.tagFilter,
                    activeTag === 'all' && styles.tagFilterActive,
                  )}
                >
                  All posts
                </a>
              </Link>
              {allTags.map((tag, index) => {
                return (
                  <Link key={tag} href={`/blog/tag/${tag}/`}>
                    <a
                      className={cx(
                        styles.tagFilter,
                        activeTag === tag && styles.tagFilterActive,
                        !showAllTags &&
                          index >= TAG_LIMIT &&
                          styles.tagFilterDesktopHidden,
                      )}
                    >
                      {formatTag(tag)}
                    </a>
                  </Link>
                );
              })}
            </div>
            {hasMoreTags && (
              <button
                type='button'
                className={styles.showMoreBtn}
                onClick={() => {
                  return setShowAllTags((prev) => {
                    return !prev;
                  });
                }}
              >
                {showAllTags ? 'See less categories' : 'See all categories'}
              </button>
            )}
          </aside>

          <main className={styles.content}>
            {adaptedPosts.length > 0 && (
              <div className={styles.grid}>
                {adaptedPosts.map((post, i) => {
                  return <PostCard {...post} key={`grid-${i}`} />;
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className={styles.paginationWrapper}>
                <Pagination
                  currentPage={pageNum}
                  totalPages={totalPages}
                  basePath={basePath}
                />
              </div>
            )}
          </main>
        </div>
      </div>
      <LegacyScripts />
      <ContentfulToolbar />
    </TemplateWrapper>
  );
};

export default BlogOverviewTemplate;
